/**
 * @module cercaliagl/Geolocation
 */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import EventEmitter from 'eventemitter3';
import { IMG } from './globals';
import LonLat from './LonLat';
import { createCircleGeometry } from './util';
/**
 * @enum {string}
 */
export var EventType = {
    TRACKING: 'tracking',
    CHANGE: 'change',
};
/**
 * @constant
 * @type {string}
 */
export var Icon = "".concat(IMG, "/geolocation.png");
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const geolocation = new cercaliagl.Geolocation();
 * ```
 *
 * Utility the get current geolocation. User browser must be allow to get GPS position.
 * It is possible to handle an event when position is changed.
 *
 * Example usage.
 *
 * ```
 * const map = new Map({
 *   target: 'map',
 *   controls: [],
 * });
 *
 * map.whenReady(() => {
 *   const geolocation = new cercaliagl.Geolocation();
 *   geolocation.setTracking(true);
 *   geolocation.on('change', () => {
  *     const marker = new cercaliagl.Marker({
 *       position: geolocation.getPosition(),
 *       icon: new cercaliagl.Icon({ src: 'resources/img/geolocation.png' }),
 *     });
 *     const geometry = geolocation.getAccuracyGeometry();
 *     const feature = new cercaliagl.Feature({ geometry: geometry });
 *     map.addMarker(marker);
 *     map.addFeature(feature);
 *     map.centerToFeatures([feature]);
 *   });
 * });
 * ```
 *
 * @extends {EventEmitter}
 * @api
 */
var Geolocation = /** @class */ (function (_super) {
    __extends(Geolocation, _super);
    function Geolocation() {
        var _this = _super.call(this) || this;
        /**
         * @private
         * @type {LonLat}
         */
        _this.position_ = null;
        /**
         * @private
         * @type {boolean}
         */
        _this.tracking_ = false;
        _this.on(EventType.TRACKING, _this.handleTrackingChanged_.bind(_this));
        return _this;
    }
    /**
     * @private
     */
    Geolocation.prototype.handleTrackingChanged_ = function () {
        if ('geolocation' in navigator) {
            var tracking = this.getTracking();
            if (tracking && this.watchId_ === undefined) {
                this.watchId_ = navigator.geolocation.watchPosition(this.positionChange_.bind(this), function (error) { return console.error('Error getting geolocation position', error); }, // eslint-disable-line no-console
                {});
            }
            else if (!tracking && this.watchId_ !== undefined) {
                navigator.geolocation.clearWatch(this.watchId_);
                this.watchId_ = undefined;
            }
        }
    };
    /**
     * @return {boolean} Tracking
     * @api
     */
    Geolocation.prototype.getTracking = function () {
        return this.tracking_;
    };
    /**
     * @param {boolean} tracking Tracking
     * @api
     */
    Geolocation.prototype.setTracking = function (tracking) {
        this.tracking_ = tracking;
        this.emit(EventType.TRACKING, this.tracking_);
    };
    /**
     * @return {LonLat}
     * @api
     */
    Geolocation.prototype.getPosition = function () {
        return this.position_;
    };
    /**
     * @return {number}
     * @api
     */
    Geolocation.prototype.getAccuracy = function () {
        return this.accuracy_;
    };
    /**
     * @return {number}
     * @api
     */
    Geolocation.prototype.getAltitudeAccuracy = function () {
        return this.altitudeAccuracy_;
    };
    /**
     * @return {number}
     * @api
     */
    Geolocation.prototype.getSpeed = function () {
        return this.speed_;
    };
    /**
     * @return {number}
     * @api
     */
    Geolocation.prototype.getHeading = function () {
        return this.heading_;
    };
    /**
     * @private
     * @param {Position} position position event.
     */
    Geolocation.prototype.positionChange_ = function (position) {
        var _a = position.coords, accuracy = _a.accuracy, altitude = _a.altitude, altitudeAccuracy = _a.altitudeAccuracy, heading = _a.heading, latitude = _a.latitude, longitude = _a.longitude, speed = _a.speed;
        this.accuracy_ = accuracy;
        this.altitude_ = altitude;
        this.altitudeAccuracy_ = altitudeAccuracy;
        this.heading_ = heading;
        this.position_ = new LonLat(longitude, latitude);
        this.speed_ = speed;
        this.emit(EventType.CHANGE);
    };
    /**
     * @api
     * @return {import("./util").GeometryGeoJSON} geometry
     */
    Geolocation.prototype.getAccuracyGeometry = function () {
        return createCircleGeometry(this.position_, this.accuracy_);
    };
    return Geolocation;
}(EventEmitter));
export default Geolocation;
//# sourceMappingURL=Geolocation.js.map